import { memo } from "react";
import { cn } from "@/lib/utils";
import { Music, User, Sparkles, Globe, Loader2, Check } from "lucide-react";

export interface StatusStep {
  step: string;
  message: string;
  done?: boolean;
}

export interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
  isTyping?: boolean;
  statusSteps?: StatusStep[];
}

interface ChatMessageProps {
  message: Message;
}

export const ChatMessage = memo(function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "user";
  const isSystem = message.role === "system";

  if (isSystem) {
    return (
      <div className="flex justify-center animate-fade-in">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent text-[11px] font-bold uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" />
          <span>{message.content}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex gap-3 animate-fade-in",
        isUser ? "flex-row-reverse" : "flex-row"
      )}
    >
      <div
        className={cn(
          "flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center shadow-lg",
          isUser
            ? "bg-gradient-to-br from-plum to-electric"
            : "bg-gradient-to-br from-white/10 to-white/5 border border-white/10"
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-primary-foreground" />
        ) : (
          <Music className="w-4 h-4 text-accent" />
        )}
      </div>

      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-4 py-3 shadow-xl",
          isUser
            ? "bg-plum/90 text-primary-foreground rounded-tr-none border border-plum-light/20"
            : "bg-[#1a1f26] border border-white/5 text-foreground rounded-tl-none"
        )}
      >
        {message.statusSteps && message.statusSteps.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {message.statusSteps.map((step, i) => {
              const isExa = step.step.startsWith("exa");
              const Icon = isExa ? Globe : Sparkles;
              return (
                <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                  {step.done ? (
                    <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                  ) : (
                    <Loader2 className="w-3 h-3 text-accent animate-spin flex-shrink-0" />
                  )}
                  <Icon className="w-3 h-3 flex-shrink-0" />
                  <span className={step.done ? "text-muted-foreground/70" : "text-muted-foreground"}>{step.message}</span>
                </div>
              );
            })}
          </div>
        )}
        {message.isTyping && !message.statusSteps?.length ? (
          <div className="flex items-center gap-1.5 py-1">
            <div className="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "150ms" }} />
            <div className="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        ) : message.content ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap font-medium">{message.content}</p>
        ) : null}
      </div>
    </div>
  );
});

ChatMessage.displayName = 'ChatMessage';
